import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
  AiAgentsNamespaceGetAiAgentSessionArgs,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { AI_AGENT_SESSION_OVERVIEW_QUERY } from 'src/components/pages/ai-agents/agents/sessions/queries';
import { AiAgentSessionDetailSections } from 'src/components/pages/conversations/components/contact-interactions/items/contact-interaction-extra-details/ai-agent-session-details';
import { InteractionDetailsDrawer } from 'src/components/pages/conversations/components/contact-interactions/items/contact-interaction-extra-details/interaction-details-drawer';
import { SingleSessionView } from 'src/components/pages/conversations/components/contact-interactions/items/contact-interaction-extra-details/shared';
import { CallAnalysisDetails } from 'src/components/pages/conversations/components/contact-interactions/items/phone-call-analysis/components/call-analysis-details';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { VisibilityHandler } from 'src/hooks/use-visible';

export const SessionOverviewDrawer = memo<{
  sessionId: string;
  visibilityHandler: VisibilityHandler;
}>(({ sessionId, visibilityHandler }) => {
  const { t } = useTranslation(['aiAgents']);
  const { id: companyId } = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();

  const { data } = useQuery<
    Pick<Query, 'aiAgents'>,
    AiAgentsNamespaceGetAiAgentSessionArgs
  >(AI_AGENT_SESSION_OVERVIEW_QUERY, {
    variables: { input: { companyId, id: sessionId } },
    onError: handleGraphQLError,
  });

  const session = data?.aiAgents.getAIAgentSession;
  const sessions = useMemo(() => (session ? [session] : []), [session]);
  const singleSessionView = useMemo<SingleSessionView>(
    () => ({
      summaryTitle: t('aiAgents:pages.agentDetails.sessions.overview.summary'),
      sentimentsTitle: t('aiAgents:pages.agentDetails.sessions.overview.sentiments'),
      goalsTitle: (achieved, total) =>
        t('aiAgents:pages.agentDetails.sessions.overview.goals', { achieved, total }),
      actionsTitle: t('aiAgents:pages.agentDetails.sessions.overview.actions'),
    }),
    [t],
  );

  if (!session) {
    return null;
  }

  const recordingUrl = session.contactInteraction?.recording?.url;
  const callDetail = session.contactInteraction?.callDetail;

  return (
    <InteractionDetailsDrawer
      testId="ai-agent-session-overview-drawer"
      visibilityHandler={visibilityHandler}
      title={t('aiAgents:pages.agentDetails.sessions.overview.title')}
      content={
        recordingUrl ? (
          <CallAnalysisDetails
            recordingSrc={recordingUrl}
            summaryText={session.summary ?? undefined}
            sentimentAnalysis={callDetail?.sentimentAnalysis ?? undefined}
            transcription={callDetail?.transcription ?? undefined}
            aiAgentSessions={sessions}
            singleSessionView={singleSessionView}
          />
        ) : (
          <AiAgentSessionDetailSections
            aiAgentSession={session}
            singleSessionView={singleSessionView}
          />
        )
      }
    />
  );
});
